import { FormProvider, useForm } from "react-hook-form";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "@/forms/RegisterPatientForm/PatientIdentifierInput";
import SiteSelect, {
  type SiteSelectFields,
} from "@/forms/RegisterPatientForm/SiteSelect";
import Form from "@/components/Form/Form";
import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { generateQueryString } from "@/api/Helpers";
import {
  fetchCareEpisodes,
  normalizePatientIdentifier,
  patientIdentifierQueryParamKey,
  sitesQueryOptions,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { deducedError } from "@/Utils/ErrorUtils";
import { useUserProfile } from "@/api/Users";
import { primaryPatientIdentifierType } from "@medoma/models/organizations";
import {
  patientStatusSchema,
  personalIdentityNumberTypeSchema,
} from "@medoma/models/patients";
import type { SiteId } from "@medoma/models/sites";

type IdentifierCheckFields = PatientIdentifierInputFields & SiteSelectFields;

const useCheckCareEpisode = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organization } = useUserProfile();
  const primaryIdentifierType = primaryPatientIdentifierType(organization);

  const [isFetchingCareEpisode, setIsFetchingCareEpisode] = useState(false);
  const [fetchCareEpisodeError, setFetchCareEpisodeError] = useState<
    string | null
  >(null);

  const checkCareEpisode = async ({
    rawIdentifier,
    isTemporaryNumber,
    siteId,
  }: {
    rawIdentifier: string;
    isTemporaryNumber: boolean;
    siteId: SiteId;
  }) => {
    const identifierType = isTemporaryNumber
      ? personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber
      : primaryIdentifierType;
    const identifier = normalizePatientIdentifier({
      identifier: rawIdentifier,
      identifierType,
    });
    const identifierParamKey = patientIdentifierQueryParamKey[identifierType];
    const queryParams = {
      limit: 1,
      sort: "desc" as const,
      [identifierParamKey]: identifier,
    };

    if (fetchCareEpisodeError !== null) {
      setFetchCareEpisodeError(null);
    }
    setIsFetchingCareEpisode(true);

    try {
      const careEpisodes = await fetchCareEpisodes({ queryParams });
      setIsFetchingCareEpisode(false);

      if (careEpisodes.length === 0) {
        void navigate(
          `new-prospect${generateQueryString({
            siteId,
            [identifierParamKey]: identifier,
          })}`,
        );
        return;
      }

      const latestCareEpisodeOnCurrentSite = careEpisodes.find(
        (careEpisode) => careEpisode.site.id === siteId,
      );
      const hasActiveCareEpisodeOnCurrentSite =
        latestCareEpisodeOnCurrentSite?.status ===
          patientStatusSchema.enum.prospect ||
        latestCareEpisodeOnCurrentSite?.status ===
          patientStatusSchema.enum.admitted;

      if (hasActiveCareEpisodeOnCurrentSite) {
        setFetchCareEpisodeError(
          t`En patient med identifierare ${rawIdentifier} är redan aktiv.`,
        );
        return;
      }

      const latestCareEpisode = careEpisodes[0]!;

      queryClient.setQueryData(
        ["patientLatestCareEpisode", rawIdentifier, queryParams],
        latestCareEpisode,
      );

      void navigate(
        `returning${generateQueryString({
          siteId,
          [identifierParamKey]: identifier,
        })}`,
      );
    } catch (error: unknown) {
      setIsFetchingCareEpisode(false);
      setFetchCareEpisodeError(deducedError(error));
    }
  };

  return { checkCareEpisode, isFetchingCareEpisode, fetchCareEpisodeError };
};

const ContinueButton = ({ isPending }: { isPending: boolean }) => (
  <FilledButton type="submit" width="fill-container" disabled={isPending}>
    {isPending ? <Spinner /> : null}
    <Trans>Fortsätt</Trans>
  </FilledButton>
);

export const PatientIdentifierCheck = () => {
  const { t } = useLingui();

  const {
    data: sites,
    isPending: isPendingSites,
    isError: isErrorSites,
  } = useQuery(sitesQueryOptions);

  if (isPendingSites) {
    return <Loading message={t`Hämtar uppgifter om enheten`} />;
  }

  if (isErrorSites) {
    return (
      <ErrorMessage
        message={t`Kunde inte hämta enheter. Försök igen om en liten stund. Kontakta support om problemet kvarstår.`}
      />
    );
  }

  if (sites.length === 0) {
    return (
      <ErrorMessage
        message={t`Ingen enhet hittades. Kontakta support om problemet kvarstår.`}
      />
    );
  }

  // With a single site there's nothing for the user to pick, so siteId stays
  // out of form state entirely and gets passed as a prop. Multi-site forms
  // keep siteId as a real RHF field driven by SiteSelect.
  return sites.length === 1 ? (
    <SingleSitePatientIdentifierCheckForm siteId={sites[0]!.id} />
  ) : (
    <MultiSitePatientIdentifierCheckForm />
  );
};

const SingleSitePatientIdentifierCheckForm = ({
  siteId,
}: {
  siteId: SiteId;
}) => {
  const { checkCareEpisode, isFetchingCareEpisode, fetchCareEpisodeError } =
    useCheckCareEpisode();

  const methods = useForm<PatientIdentifierInputFields>({
    defaultValues: {
      identifier: "",
      isTemporaryNumber: false,
    },
  });

  const validateAndSubmit = methods.handleSubmit((formData) => {
    void checkCareEpisode({
      rawIdentifier: formData.identifier,
      isTemporaryNumber: formData.isTemporaryNumber,
      siteId,
    });
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <Form.Section>
          <PatientIdentifierInput />
        </Form.Section>
        <ContinueButton isPending={isFetchingCareEpisode} />
      </Form>
      {fetchCareEpisodeError ? (
        <ErrorMessage message={fetchCareEpisodeError} />
      ) : null}
    </FormProvider>
  );
};

const MultiSitePatientIdentifierCheckForm = () => {
  const { checkCareEpisode, isFetchingCareEpisode, fetchCareEpisodeError } =
    useCheckCareEpisode();

  const methods = useForm<IdentifierCheckFields>({
    defaultValues: {
      identifier: "",
      isTemporaryNumber: false,
      siteId: "",
    },
  });

  const validateAndSubmit = methods.handleSubmit((formData) => {
    void checkCareEpisode({
      rawIdentifier: formData.identifier,
      isTemporaryNumber: formData.isTemporaryNumber,
      // SiteSelect's required validator guarantees a picked site before submit.
      siteId: formData.siteId as SiteId,
    });
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <Form.Section>
          <SiteSelect />
          <PatientIdentifierInput />
        </Form.Section>
        <ContinueButton isPending={isFetchingCareEpisode} />
      </Form>
      {fetchCareEpisodeError ? (
        <ErrorMessage message={fetchCareEpisodeError} />
      ) : null}
    </FormProvider>
  );
};
